// src/components/call-sessions/CallSessionDetails.jsx
/**
 * CallSessionDetails Component
 *
 * Modal component that displays detailed call session information including audio playback,
 * symptom assessments, and patient call history navigation.
 *
 * Functions:
 * - getSymptomDataMemoized: Processes and memoizes symptom observation data from API
 */

import React, { useMemo } from 'react';
import { useCallSessionContext } from '../../context/CallSessionContext';
import Modal from '../common/Modal';
import CallSessionSidebar from './CallSessionSidebar';
import SymptomCard from './SymptomCard';
import SectionHeader from '../common/SectionHeader';
import { formatDate } from '../../utils/dateUtils';
import { processSymptomData } from '../../utils/symptomUtils';

const CallSessionDetails = () => {
  const {
    selectedCallSession,
    patientCallSessions,
    showDetails,
    closeDetails,
    loading,
  } = useCallSessionContext();

  const getSymptomDataMemoized = useMemo(() => {
    if (!selectedCallSession) return {};
    return processSymptomData(selectedCallSession.call_session_observations);
  }, [selectedCallSession?.call_session_observations]);

  if (!showDetails) return null;

  if (loading) {
    return (
      <Modal isOpen={showDetails} onClose={closeDetails} size="xl">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  if (!selectedCallSession) {
    return (
      <Modal isOpen={showDetails} onClose={closeDetails} size="xl">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Call Sessions Found</h3>
          <p className="text-gray-600">This patient doesn't have any call sessions yet.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={showDetails} onClose={closeDetails} size="xl">
      <div className="flex h-[80vh]">
        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Call Session Details
            </h2>
            <p className="text-gray-600">
              Patient: {selectedCallSession.patient?.first_name} {selectedCallSession.patient?.last_name}
            </p>
          </div>

          {/* Call Session Info Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <div className="text-sm text-gray-600">Call Status</div>
            <div className="text-lg font-semibold text-blue-600 capitalize">{selectedCallSession.status}</div>
          </div>
          <div className={`p-4 rounded-lg text-center ${
            selectedCallSession.patient_assessment_flag === 'stable'
              ? 'bg-green-200'
              : selectedCallSession.patient_assessment_flag === 'critical'
              ? 'bg-red-200'
              : selectedCallSession.patient_assessment_flag === 'follow-up'
              ? 'bg-yellow-200'
              : 'bg-gray-50'
          }`}>
            <div className="text-sm text-gray-600">Patient Status</div>
            <div className={`text-lg font-semibold ${
              selectedCallSession.patient_assessment_flag === 'stable'
                ? 'text-green-600'
                : selectedCallSession.patient_assessment_flag === 'critical'
                ? 'text-red-600'
                : selectedCallSession.patient_assessment_flag === 'follow-up'
                ? 'text-yellow-600'
                : 'text-gray-600'
            }`}>
              {selectedCallSession.patient_assessment_flag
              ? selectedCallSession.patient_assessment_flag.charAt(0).toUpperCase() + selectedCallSession.patient_assessment_flag.slice(1)
              : 'Unknown'}
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg text-center">
            <div className="text-sm text-gray-600">Call Date</div>
            <div className="text-lg font-semibold text-purple-600">
              {formatDate(selectedCallSession.call_date)}
            </div>
          </div>
        </div>

          <SectionHeader title="Call Summary">
            <div className="bg-gray-50 p-4 rounded-lg">
              {selectedCallSession.call_summary_report ? (
                <div className="prose max-w-none">
                  <div className="bg-white p-4 rounded-lg border">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {selectedCallSession.call_summary_report}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-white p-4 rounded-lg border text-center">
                  <p className="text-gray-500">Call Summary Not Available</p>
                </div>
              )}
            </div>
          </SectionHeader>

          {/* Audio Transcript */}
          <SectionHeader title="Audio Transcript">
            <div className="bg-gray-50 p-4 rounded-lg">
              {selectedCallSession.audio_transcript ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Audio file available:</p>
                  <audio
                    controls
                    className="w-full"
                    preload="metadata"
                    key={`audio-${selectedCallSession.id}`}
                    onLoadedMetadata={(e) => {
                    }}
                    onError={(e) => {
                      console.error('Audio load error:', e);
                    }}
                  >
                    <source src={selectedCallSession.audio_transcript} type="audio/mpeg" />
                    Your browser doesn't support audio playback.
                  </audio>
                </div>
              ) : (
                <p className="text-gray-500">No audio transcript available for this call session.</p>
              )}
            </div>
          </SectionHeader>

          {/* Symptoms Assessment - displays whatever API returns */}
          <SectionHeader title="Symptoms Assessment">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-4">
                {Object.entries(getSymptomDataMemoized).map(([symptomType, data]) => (
                  <SymptomCard
                    key={symptomType}
                    symptomType={symptomType}
                    data={data}
                  />
                ))}

                {Object.keys(getSymptomDataMemoized).length === 0 && (
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <p className="text-gray-500">No symptoms were assessed in this call session.</p>
                  </div>
                )}
              </div>
            </div>
          </SectionHeader>
        </div>

        {/* Right Sidebar */}
        <div className="w-80 border-l bg-gray-50 p-4">
          <CallSessionSidebar
            callSessions={patientCallSessions}
            selectedId={selectedCallSession?.id}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CallSessionDetails;